import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { filter, find } from 'lodash'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  File as FileIcon,
  Minus as MinusIcon,
  Plus as PlusIcon,
  UploadCloud as UploadCloudIcon,
} from 'react-feather'

import { Autocomplete, BaseDialog } from 'components'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import helpers from 'helpers'
import * as service from 'service'
import theme from 'theme'
import schema from './schema'
import useStyles from './styles'

const DisputeModal = ({
  open,
  setOpen,
  activity,
  governanceProjectId,
  onSuccess,
}) => {
  const {
    CATEGORY_DESCRIPTION_FIELDS,
    DEFAULT_AUDITOR_ALTERNATIVE_ID,
    DEFAULT_QUESTION_CODE,
    DEFAULT_REPORT_TEXT,
    DEFAULT_UNIT_ALTERNATIVE_ID,
    DISPUTE_MODAL_CONFIG,
    DISPUTE_MODAL_FEEDBACK,
    FORM_FIELDS,
    QUESTION_CODES,
    SNACKBAR_VARIANTS,
  } = constants.dai.disputes

  const question = activity?.governanceProjectQuestion
  const auditEntities =
    activity?.governanceProjectCompanyStage?.governanceProjectAuditEntities ??
    []
  const auditorAnswer = find(auditEntities, { kind: 'answer_audit' })

  // matrixAlternatives vem como ATRIBUTO do serializer (já ordenado por peso): a
  // associação question_alternatives é cortada pelo adapter :json quando a question
  // é aninhada na activity. Os ids escolhidos (unidade e auditor) também são atributos.
  const questionAlternatives = question?.matrixAlternatives ?? []

  // Unidade: resposta da submissão (coluna da question). Auditor: a alternativa
  // efetivamente atribuída na auditoria (entidade answer_audit do stage), que diverge
  // da coluna audit_alternative_answer_id (preenchida por title-match na importação).
  const defaultUnitAlternativeId =
    question?.alternativeAnswerId?.toString() ?? DEFAULT_UNIT_ALTERNATIVE_ID
  const defaultAuditorAlternativeId =
    activity?.governanceProjectCompanyStage?.auditAnswerAlternativeId?.toString() ??
    DEFAULT_AUDITOR_ALTERNATIVE_ID

  const getAlternativeOptionLabel = (alternative) =>
    alternative.adherenceLabel
      ? `${alternative.adherenceLabel} - ${alternative.title}`
      : alternative.title

  const allAttachments = (activity?.evidencePlanActions ?? [])
    .flatMap((epa) => epa?.evidence?.attachments ?? [])
    .filter((attachment) => !!attachment?.id)

  const [loading, setLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [selectedAttachments, setSelectedAttachments] = useState(allAttachments)
  const [localFiles, setLocalFiles] = useState([])
  const fileInputRef = useRef(null)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const { control, errors, handleSubmit, reset } = useForm({
    validationSchema: schema,
    defaultValues: {
      [FORM_FIELDS.QUESTION_CODE]: question?.code ?? DEFAULT_QUESTION_CODE,
      [FORM_FIELDS.AUDITOR_ALTERNATIVE_ID]: defaultAuditorAlternativeId,
      [FORM_FIELDS.UNIT_ALTERNATIVE_ID]: defaultUnitAlternativeId,
      [FORM_FIELDS.REPORT_TEXT]:
        auditorAnswer?.description ?? DEFAULT_REPORT_TEXT,
      nonConformities: filter(auditEntities, { kind: 'non_conformity' }).map(
        (entity) => ({
          [CATEGORY_DESCRIPTION_FIELDS.NAME]: entity?.name ?? '',
          [CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]: entity?.description ?? '',
        }),
      ),
      improvementOpportunities: filter(auditEntities, {
        kind: 'improvement',
      }).map((entity) => ({
        [CATEGORY_DESCRIPTION_FIELDS.NAME]: entity?.name ?? '',
        [CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]: entity?.description ?? '',
      })),
    },
  })

  const {
    fields: nonConformityFields,
    append: appendNonConformity,
    remove: removeNonConformity,
  } = useFieldArray({ control, name: 'nonConformities' })

  const {
    fields: improvementFields,
    append: appendImprovement,
    remove: removeImprovement,
  } = useFieldArray({ control, name: 'improvementOpportunities' })

  useEffect(() => {
    if (!open || !activity) return

    setSelectedAttachments(allAttachments)

    reset({
      [FORM_FIELDS.QUESTION_CODE]: question?.code ?? DEFAULT_QUESTION_CODE,
      [FORM_FIELDS.AUDITOR_ALTERNATIVE_ID]: defaultAuditorAlternativeId,
      [FORM_FIELDS.UNIT_ALTERNATIVE_ID]: defaultUnitAlternativeId,
      [FORM_FIELDS.REPORT_TEXT]:
        auditorAnswer?.description ?? DEFAULT_REPORT_TEXT,
      nonConformities: filter(auditEntities, { kind: 'non_conformity' }).map(
        (entity) => ({
          [CATEGORY_DESCRIPTION_FIELDS.NAME]: entity?.name ?? '',
          [CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]: entity?.description ?? '',
        }),
      ),
      improvementOpportunities: filter(auditEntities, {
        kind: 'improvement',
      }).map((entity) => ({
        [CATEGORY_DESCRIPTION_FIELDS.NAME]: entity?.name ?? '',
        [CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]: entity?.description ?? '',
      })),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleToggleAttachment = (attachment) => {
    setSelectedAttachments((prev) =>
      prev.some((a) => a.id === attachment.id)
        ? prev.filter((a) => a.id !== attachment.id)
        : [...prev, attachment],
    )
  }

  const handleLocalFileChange = (event) => {
    const files = Array.from(event.target.files)
    setLocalFiles((prev) => {
      const existingNames = new Set(prev.map((file) => file.name))
      return [...prev, ...files.filter((file) => !existingNames.has(file.name))]
    })
    event.target.value = ''
  }

  const removeLocalFile = (index) =>
    setLocalFiles((prev) => prev.filter((_, i) => i !== index))

  const uploadAttachments = async (questionCode) => {
    const total = selectedAttachments.length + localFiles.length
    if (total > 0) setUploadProgress({ sent: 0, total })

    const { succeeded, failed } = await helpers.dai.uploadDisputeAttachments({
      questionCode,
      governanceProjectId,
      governanceProjectActivityId: activity?.id,
      selectedAttachments,
      localFiles,
      uploadEvidence: service.dai.disputes.uploadEvidence,
      listEvidences: service.dai.disputes.listEvidences,
      reconcileEvidences: service.dai.disputes.reconcileEvidences,
      onProgress: (sent, total) => setUploadProgress({ sent, total }),
    })

    setUploadProgress(null)

    if (failed.length > 0) {
      snackbar.open({
        message: DISPUTE_MODAL_FEEDBACK.getFailedUploadsMessage(failed.length),
        variant:
          succeeded.length === 0
            ? SNACKBAR_VARIANTS.ERROR
            : SNACKBAR_VARIANTS.WARNING,
      })
    }

    return succeeded
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const citedFiles = await uploadAttachments(
        data[FORM_FIELDS.QUESTION_CODE],
      )

      const response = await service.dai.disputes.create({
        questionCode: data[FORM_FIELDS.QUESTION_CODE],
        governanceProjectId,
        governanceProjectActivityId: activity?.id,
        auditorReport: {
          alternatives: questionAlternatives.map((alternative) => ({
            id: alternative.id,
            title: alternative.title,
            adherence_label: alternative.adherenceLabel,
            weight: alternative.weight,
          })),
          auditor_alternative_id: Number(
            data[FORM_FIELDS.AUDITOR_ALTERNATIVE_ID],
          ),
          unit_alternative_id: Number(data[FORM_FIELDS.UNIT_ALTERNATIVE_ID]),
          report_text: data[FORM_FIELDS.REPORT_TEXT],
          non_conformities: (data.nonConformities ?? [])
            .filter(
              (nonConformity) =>
                !!nonConformity[CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION],
            )
            .map(({ description, name }) => ({ category: name, description })),
          improvement_opportunities: (data.improvementOpportunities ?? [])
            .filter(
              (improvementOpportunity) =>
                !!improvementOpportunity[
                  CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION
                ],
            )
            .map(({ description, name }) => ({ category: name, description })),
          cited_files: citedFiles,
        },
      })

      const runId = response?.data?.dispute?.run_id
      setOpen(false)
      onSuccess(runId)
      snackbar.open({
        message: DISPUTE_MODAL_FEEDBACK.PROCESSING_MESSAGE,
        variant: SNACKBAR_VARIANTS.INFO,
      })
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: SNACKBAR_VARIANTS.ERROR,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title="Gerar documento de contestação"
      closeButtonText="Cancelar"
      actionButtonText="Gerar contestação"
      formRef={DISPUTE_MODAL_CONFIG.FORM_ID}
      dialogSize="md"
      fullWidth
      loading={loading}
      preventCloseOnBackdropClick
    >
      <Box
        py={2}
        component="form"
        onSubmit={(event) => {
          event.stopPropagation()
          handleSubmit(onSubmit)(event)
        }}
        id={DISPUTE_MODAL_CONFIG.FORM_ID}
      >
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
          <Controller
            control={control}
            name={FORM_FIELDS.QUESTION_CODE}
            mode="onChange"
            onChange={([, value]) => value}
            as={
              <Autocomplete
                options={QUESTION_CODES}
                getOptionLabel={(option) => option}
                textFieldProps={{
                  label: 'Código da questão *',
                  variant: 'outlined',
                  error: !!errors[FORM_FIELDS.QUESTION_CODE],
                  helperText: errors[FORM_FIELDS.QUESTION_CODE]?.message,
                }}
              />
            }
          />

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Alternativa atribuída pelo auditor *
              </Typography>
              <Controller
                control={control}
                name={FORM_FIELDS.AUDITOR_ALTERNATIVE_ID}
                as={
                  <RadioGroup className={classes.alternativeGroup}>
                    {questionAlternatives.map((alternative) => (
                      <FormControlLabel
                        key={alternative.id}
                        value={alternative.id.toString()}
                        control={<Radio size="small" />}
                        label={getAlternativeOptionLabel(alternative)}
                      />
                    ))}
                  </RadioGroup>
                }
              />
              {!!errors[FORM_FIELDS.AUDITOR_ALTERNATIVE_ID] && (
                <FormHelperText error>
                  {errors[FORM_FIELDS.AUDITOR_ALTERNATIVE_ID].message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                Alternativa da unidade *
              </Typography>
              <Controller
                control={control}
                name={FORM_FIELDS.UNIT_ALTERNATIVE_ID}
                as={
                  <RadioGroup className={classes.alternativeGroup}>
                    {questionAlternatives.map((alternative) => (
                      <FormControlLabel
                        key={alternative.id}
                        value={alternative.id.toString()}
                        control={<Radio size="small" />}
                        label={getAlternativeOptionLabel(alternative)}
                      />
                    ))}
                  </RadioGroup>
                }
              />
              {!!errors[FORM_FIELDS.UNIT_ALTERNATIVE_ID] && (
                <FormHelperText error>
                  {errors[FORM_FIELDS.UNIT_ALTERNATIVE_ID].message}
                </FormHelperText>
              )}
            </Grid>
          </Grid>

          <Divider />

          <Controller
            control={control}
            name={FORM_FIELDS.REPORT_TEXT}
            as={
              <TextField
                label="Texto do relatório do auditor *"
                variant="outlined"
                fullWidth
                multiline
                minRows={5}
                placeholder="Cole aqui o texto completo do relatório do auditor..."
                error={!!errors[FORM_FIELDS.REPORT_TEXT]}
                helperText={errors[FORM_FIELDS.REPORT_TEXT]?.message}
              />
            }
          />

          <Divider />

          <Box>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Não conformidades (opcional)
            </Typography>
            {nonConformityFields.map((field, index) => (
              <Box key={field.id} className={classes.listItem}>
                <Box display="flex" flexDirection="column" flex={1} gridGap={8}>
                  <Controller
                    control={control}
                    name={`nonConformities[${index}].${CATEGORY_DESCRIPTION_FIELDS.NAME}`}
                    defaultValue={field[CATEGORY_DESCRIPTION_FIELDS.NAME]}
                    as={
                      <TextField
                        label="Nome"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    }
                  />
                  <Controller
                    control={control}
                    name={`nonConformities[${index}].${CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION}`}
                    defaultValue={
                      field[CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]
                    }
                    as={
                      <TextField
                        label="Descrição"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                      />
                    }
                  />
                </Box>
                <Tooltip title="Remover">
                  <IconButton
                    size="small"
                    className={classes.removeButton}
                    onClick={() => removeNonConformity(index)}
                  >
                    <MinusIcon size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button
              startIcon={<PlusIcon size={14} />}
              size="small"
              className={classes.addButton}
              onClick={() =>
                appendNonConformity(helpers.dai.createCategoryDescriptionItem())
              }
            >
              Adicionar não conformidade
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Oportunidades de melhoria (opcional)
            </Typography>
            {improvementFields.map((field, index) => (
              <Box key={field.id} className={classes.listItem}>
                <Box display="flex" flexDirection="column" flex={1} gridGap={8}>
                  <Controller
                    control={control}
                    name={`improvementOpportunities[${index}].${CATEGORY_DESCRIPTION_FIELDS.NAME}`}
                    defaultValue={field[CATEGORY_DESCRIPTION_FIELDS.NAME]}
                    as={
                      <TextField
                        label="Nome"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    }
                  />
                  <Controller
                    control={control}
                    name={`improvementOpportunities[${index}].${CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION}`}
                    defaultValue={
                      field[CATEGORY_DESCRIPTION_FIELDS.DESCRIPTION]
                    }
                    as={
                      <TextField
                        label="Descrição"
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                      />
                    }
                  />
                </Box>
                <Tooltip title="Remover">
                  <IconButton
                    size="small"
                    className={classes.removeButton}
                    onClick={() => removeImprovement(index)}
                  >
                    <MinusIcon size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            <Button
              startIcon={<PlusIcon size={14} />}
              size="small"
              className={classes.addButton}
              onClick={() =>
                appendImprovement(helpers.dai.createCategoryDescriptionItem())
              }
            >
              Adicionar oportunidade
            </Button>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Evidências anexadas (selecione as que serão enviadas)
            </Typography>
            {allAttachments.length === 0 ? (
              <Typography variant="body2" className={classes.emptyAttachments}>
                Nenhum anexo encontrado nas evidências desta atividade.
              </Typography>
            ) : (
              <Box className={classes.attachmentList}>
                {allAttachments.map((attachment) => (
                  <Box key={attachment.id} className={classes.attachmentItem}>
                    <Checkbox
                      size="small"
                      checked={selectedAttachments.some(
                        (selected) => selected.id === attachment.id,
                      )}
                      onChange={() => handleToggleAttachment(attachment)}
                    />
                    <FileIcon size={14} className={classes.fileIcon} />
                    <Typography variant="body2" className={classes.fileName}>
                      {attachment?.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Documentos do computador (opcional)
            </Typography>
            {localFiles.length > 0 && (
              <Box className={classes.attachmentList}>
                {localFiles.map((file, index) => (
                  <Box key={index} className={classes.attachmentItem}>
                    <FileIcon size={14} className={classes.fileIcon} />
                    <Typography variant="body2" className={classes.fileName}>
                      {file.name}
                    </Typography>
                    <Tooltip title="Remover">
                      <IconButton
                        size="small"
                        className={classes.removeButton}
                        onClick={() => removeLocalFile(index)}
                      >
                        <MinusIcon size={14} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
            <Button
              color="primary"
              variant="outlined"
              startIcon={<UploadCloudIcon />}
              className={classes.attachmentButton}
              onClick={() => fileInputRef.current?.click()}
              fullWidth
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={DISPUTE_MODAL_CONFIG.FILE_ACCEPT}
                className={classes.inputFile}
                onChange={handleLocalFileChange}
              />
              Selecionar Arquivos
            </Button>
          </Box>

          {uploadProgress && (
            <Box className={classes.uploadProgress}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
              >
                <Typography
                  variant="body2"
                  className={classes.uploadProgressText}
                >
                  Enviando arquivos...
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.uploadProgressText}
                >
                  {uploadProgress.sent} de {uploadProgress.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  uploadProgress.total > 0
                    ? (uploadProgress.sent / uploadProgress.total) * 100
                    : 0
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    </BaseDialog>
  )
}

DisputeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  activity: PropTypes.object,
  // Vem de match.params (rota) → string; aceita number também.
  governanceProjectId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSuccess: PropTypes.func.isRequired,
}

export default DisputeModal
